import React, { useMemo } from 'react';
import {
  BAUSTEIN_BASIS,
  BAUSTEIN_MODULE,
  BAUSTEIN_MUSTER,
  BAUSTEIN_VORLAGEN,
  type BausteinZeile,
} from '../portalBausteinSchema';
import { PORTAL_NAV_BAUSTEIN_LABEL } from '../portalNavigation';
import {
  BAUSTEIN_DOT_ON,
  BAUSTEIN_NAME_OFF,
  BAUSTEIN_NAME_ON,
  BAUSTEIN_STATUS_CLASS_OFF,
  BAUSTEIN_STATUS_CLASS_ON,
  BAUSTEIN_STATUS_OFF,
  BAUSTEIN_STATUS_ON,
  bausteinRowIsGreen,
  resolveBausteinUnlock,
  type BausteinSeatInput,
} from '../portalBausteinUnlock';

const BAUSTEIN_GROUP_ON = 'Ihre Bausteine';
const BAUSTEIN_GROUP_OFF = 'Weitere';

/** Schwarzer Grund, feine lila Kante — wie die Akte. Volle Fläche, ruhige Zeilen. */
const BAUSTEIN_GROUND = 'bg-[#07070a]';
const BAUSTEIN_EDGE = 'rounded-lg border border-violet-400/30 bg-[#07070a]';
const BAUSTEIN_SHELL =
  `flex h-full min-h-0 w-full min-w-0 max-w-none flex-1 flex-col overflow-y-auto ${BAUSTEIN_GROUND} ${BAUSTEIN_EDGE}`;
const BAUSTEIN_ROW_ON = 'flex items-center gap-3 border-b border-violet-400/20 bg-transparent px-1 py-2';
const BAUSTEIN_ROW_OFF = 'flex items-center gap-3 border-b border-violet-400/15 bg-transparent px-1 py-2';

function uniqueZeilen(): BausteinZeile[] {
  const seen = new Set<string>();
  const out: BausteinZeile[] = [];
  const push = (zeile: BausteinZeile) => {
    if (seen.has(zeile.name)) return;
    seen.add(zeile.name);
    out.push({ name: zeile.name });
  };
  for (const zeile of BAUSTEIN_BASIS) push(zeile);
  for (const zeile of BAUSTEIN_MODULE) push(zeile);
  for (const vorlage of BAUSTEIN_VORLAGEN) {
    push({ name: vorlage.name });
    for (const zeile of vorlage.zeilen) push(zeile);
  }
  for (const zeile of BAUSTEIN_MUSTER) push(zeile);
  return out;
}

function Zeile({ name, seat }: BausteinZeile & { seat: BausteinSeatInput }) {
  const on = bausteinRowIsGreen(resolveBausteinUnlock(name, seat));
  return (
    <li
      className={on ? BAUSTEIN_ROW_ON : BAUSTEIN_ROW_OFF}
      data-baustein-name={name}
      data-baustein-unlock={on ? 'on' : 'off'}
    >
      {on ? <span className={BAUSTEIN_DOT_ON} aria-hidden /> : <span className="mr-2 inline-block h-2.5 w-2.5 shrink-0" aria-hidden />}
      <p className={`min-w-0 flex-1 truncate ${on ? BAUSTEIN_NAME_ON : BAUSTEIN_NAME_OFF}`}>{name}</p>
      <p className={`shrink-0 !mt-0 ${on ? BAUSTEIN_STATUS_CLASS_ON : BAUSTEIN_STATUS_CLASS_OFF}`}>
        {on ? BAUSTEIN_STATUS_ON : BAUSTEIN_STATUS_OFF}
      </p>
    </li>
  );
}

export const BausteinView: React.FC<{ seat?: BausteinSeatInput }> = ({ seat = {} }) => {
  const { on, off } = useMemo(() => {
    const unlocked: BausteinZeile[] = [];
    const locked: BausteinZeile[] = [];
    for (const zeile of uniqueZeilen()) {
      if (bausteinRowIsGreen(resolveBausteinUnlock(zeile.name, seat))) unlocked.push(zeile);
      else locked.push(zeile);
    }
    return { on: unlocked, off: locked };
  }, [seat]);

  return (
    <div data-testid="portal-baustein-pane" className={`${BAUSTEIN_SHELL} px-4 py-5 sm:px-6`}>
      <h1 className="text-2xl font-bold text-white">{PORTAL_NAV_BAUSTEIN_LABEL}</h1>
      <p className="mt-1 text-slate-400">Was freigeschaltet ist, und was noch dazukommen kann.</p>

      <section className="mt-6" aria-labelledby="baustein-ihre">
        <h2 id="baustein-ihre" className="text-sm font-semibold text-violet-100">
          {BAUSTEIN_GROUP_ON}
        </h2>
        {on.length === 0 ? (
          <p className="mt-2 text-sm text-slate-400">Noch nichts freigeschaltet.</p>
        ) : (
          <ul className="mt-1">
            {on.map((zeile) => (
              <Zeile key={zeile.name} name={zeile.name} seat={seat} />
            ))}
          </ul>
        )}
      </section>

      <section className="mt-6" aria-labelledby="baustein-weitere">
        <h2 id="baustein-weitere" className="text-sm font-semibold text-slate-300">
          {BAUSTEIN_GROUP_OFF}
        </h2>
        {off.length === 0 ? (
          <p className="mt-2 text-sm text-slate-400">Nichts weiter offen.</p>
        ) : (
          <ul className="mt-1">
            {off.map((zeile) => (
              <Zeile key={zeile.name} name={zeile.name} seat={seat} />
            ))}
          </ul>
        )}
      </section>
    </div>
  );
};
